import React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import TextController from "../form-fields/TextController";
import SelectController from "../form-fields/SelectController";
import { useAuth } from "../../lib/AuthContext";

const inviteSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Name is required.")
    .min(2, "Name must be at least 2 characters."),
  email: z.string().email("Invalid email address"),
  role: z.string().optional(),
});

type InviteValues = z.infer<typeof inviteSchema>;

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (data: InviteValues) => void;
}

const InviteModal: React.FC<InviteModalProps> = ({
  isOpen,
  onClose,
  onInvite,
}) => {
  const auth = useAuth();
  const roles = auth.roles.length
    ? auth.roles.map((role: any) => ({
        label: role.role,
        value: role.id.toString(),
      }))
    : [];
  // console.log(roles, 999);
  const form = useForm<InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      name: "",
      email: "",
      role: "2",
    },
  });

  const onSubmit = (data: InviteValues) => {
    onInvite(data);
    form.reset();
    onClose(); // Ensure the modal is closed after submission
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite an Agent</DialogTitle>
          <DialogDescription>
            Specify the agent's name and email to send an invite.
          </DialogDescription>
        </DialogHeader>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <TextController
              name="name"
              label="Name"
              placeholder="Enter agent's name"
            />
            <TextController
              name="email"
              label="Email"
              placeholder="Enter agent's email"
              type="email"
            />
            <SelectController
              name="role"
              label="User Role"
              options={roles}
              defaultValue={"2"}
            />
            <DialogFooter>
              <Button type="submit" id="invite-team-member">
                Send Invite
              </Button>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default InviteModal;
